import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getBroadcastGroupDetails, getBroadcastGroupExecutions } from '@/services/api/broadcast';
import { BroadcastDetailsModal } from '@/components/broadcast-details-modal';
import { ModalErrorBoundary } from '@/components/broadcast-details-modal/ErrorBoundary';
import type { ExecutionItem, GroupBroadcast } from '@/components/broadcast-details-modal/types';

interface ViewBroadcastGroupModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    groupBroadcastId: number | null;
}

/** Uma página por broadcast, e a tela precisa de todas de uma vez. */
const PAGINAS_POR_GRUPO = 500;

const ATIVOS = new Set(['pending', 'processing', 'sending']);

/**
 * Casca do modal de detalhes: busca os dados e entrega ao componente de UI.
 *
 * A tela em si vive em `@/components/broadcast-details-modal`, que é
 * autocontido (só React + CSS Module) e não sabe de react-query, axios nem dos
 * tipos deste projeto. Este arquivo é a única costura entre os dois: mantém a
 * assinatura antiga (`open`/`onOpenChange`/`groupBroadcastId`), então nenhum
 * ponto de uso precisou mudar.
 */
export function ViewBroadcastGroupModal({ open, onOpenChange, groupBroadcastId }: ViewBroadcastGroupModalProps) {
    const [deveRepetir, setDeveRepetir] = useState(false);

    useEffect(() => { if (!open) setDeveRepetir(false); }, [open]);
    useEffect(() => { setDeveRepetir(false); }, [groupBroadcastId]);

    const { data: details, isLoading: carregandoDetalhe } = useQuery({
        queryKey: ['broadcast-group-details', groupBroadcastId],
        queryFn: () => getBroadcastGroupDetails(groupBroadcastId!),
        enabled: open && groupBroadcastId !== null,
        staleTime: Infinity,
        refetchOnWindowFocus: false,
        refetchInterval: false,
    });

    const { data: execucoes, isLoading: carregandoExecucoes } = useQuery({
        queryKey: ['broadcast-group-executions', groupBroadcastId],
        queryFn: () => getBroadcastGroupExecutions(groupBroadcastId!, 1, PAGINAS_POR_GRUPO),
        enabled: open && groupBroadcastId !== null,
        staleTime: Infinity,
        refetchOnWindowFocus: false,
        // Grupo ainda rodando se atualiza sozinho a cada 15s.
        refetchInterval: deveRepetir ? 15000 : false,
    });

    const itens = useMemo(
        () => (execucoes?.items ?? []) as unknown as ExecutionItem[],
        [execucoes],
    );

    useEffect(() => {
        setDeveRepetir(itens.some(e => ATIVOS.has(e.status)));
    }, [itens]);

    // O response de execuções traz `app_id`, nunca o nome. O nome vem do
    // `rate_limit_by_app` do detalhe, que já é calculado no backend — sem isso
    // a tela mostraria "App 69" onde deveria estar "Marone-Fivni-g15-p59".
    const nomesDeApp = useMemo(() => {
        const mapa: Record<number, string> = {};
        for (const a of details?.rate_limit_by_app ?? []) {
            if (a.app_id != null && a.app_name) mapa[a.app_id] = a.app_name;
        }
        return mapa;
    }, [details]);

    const grupo = useMemo<GroupBroadcast | null>(() => {
        if (!details) return null;
        return {
            ...details,
            title: details.title ?? `Broadcast ${details.group_broadcast_id}`,
            user_name: details.user_name ?? '',
            message_tag: details.message_tag ?? '',
            target_language: details.target_language ?? null,
            total_delivered: details.total_delivered ?? 0,
        } as GroupBroadcast;
    }, [details]);

    if (!open || groupBroadcastId === null) return null;

    return (
        <ModalErrorBoundary onClose={() => onOpenChange(false)}>
            <BroadcastDetailsModal
                open={open}
                onClose={() => onOpenChange(false)}
                group={grupo}
                items={itens}
                loading={carregandoDetalhe || carregandoExecucoes}
                appNames={nomesDeApp}
                defaultTab="charts"
            />
        </ModalErrorBoundary>
    );
}
